import { takeLatest, call, put, select, delay } from "redux-saga/effects";

import API_ENDPOINTS from '@services/apiConfig';
import AuthManager from '@services/AuthManager';
import axios from 'axios';
import { encodeToUrlParams } from "@utils/common";
import { dialogActions, toastActions, merchantActions, uiActions, merchantSelectors } from '.';

//  Action types
export const actionTypes = {
  fetch: 'serviceAgreement/INITIATE',
  fetchSuccess: 'serviceAgreement/SUCCESS',
  fetchFailed: 'serviceAgreement/FAILED',
  sendOtp: 'serviceAgreementOTP/INITIATE',
  verifyOtp: 'serviveAgreementVerifyOTP/INITIATE',
  verifyOtpSuccess: 'serviveAgreementVerifyOTP/SUCCESS',
  verifyOtpFailed: 'serviveAgreementVerifyOTP/FAILED',
  getEsignedAgreement: 'esignedAgreement/INITIATE',
  getEsignedAgreementSuccess: 'esignedAgreement/SUCCESS',
  getEsignedAgreementFailed: 'esignedAgreement/FAILED'
};

const initialState = {
  generatedMerged: {
    response: null,
    processing: false,
  },
  verifyOtp: {
    processing: false,
    error: null,
  },
  esignedAgreement: {
    response: null,
    processing: false,
  }
};

//  Reducer
export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.fetch:
      return { ...state, generatedMerged: { processing: true, response: null } };
    case actionTypes.fetchFailed:
    case actionTypes.fetchSuccess:
      return { ...state, generatedMerged: { response: action.payload, processing: false } };
    case actionTypes.verifyOtp:
      return { ...state, verifyOtp: { processing: true, error: null } };
    case actionTypes.verifyOtpSuccess:
      return { ...state, verifyOtp: { processing: false, error: null } };
    case actionTypes.verifyOtpFailed:
      return { ...state, verifyOtp: { processing: false, error: action.payload } };
    case actionTypes.getEsignedAgreement:
      return { ...state, esignedAgreement: { processing: true, response: null } };
    case actionTypes.getEsignedAgreementSuccess:
    case actionTypes.getEsignedAgreementFailed:
      return { ...state, esignedAgreement: { processing: false, response: action.payload } };
    default: {
      return state;
    }
  }
};

//  Selectors
export const selectors = {
  getDetails: (state) => state.onboardingApp.serviceAgreement.generatedMerged.response,
  msaProcessing: (state) => state.onboardingApp.serviceAgreement.generatedMerged.processing,
  verifyProcessing: (state) => state.onboardingApp.serviceAgreement.verifyOtp.processing,
  verifyOtpErr: (state) => state.onboardingApp.serviceAgreement.verifyOtp.error,
  getEsignedAgreementProcessing: (state) => state.onboardingApp.serviceAgreement.esignedAgreement.processing,
};

//  Actions
export const actions = {
  fetch: () => ({ type: actionTypes.fetch }),
  sendOtp: (kycDocumentUuid) => ({
    type: actionTypes.sendOtp,
    payload: { kycDocumentUuid }
  }),
  verifyOtp: (otp, kycDocumentUuid) => ({
    type: actionTypes.verifyOtp,
    payload: { otp, kycDocumentUuid },
  }),
  getEsignedAgreement: () => ({ type: actionTypes.getEsignedAgreement }),
};

// Workers
function* fetchWorker() {
  try {
    const response = yield call(
      [axios, 'get'],
      API_ENDPOINTS.SERVICE_AGREEMENT.GENERATE_MERGED_DOCUMENT_ESIGN(AuthManager.getMerchantUUID()),
    ) || {};
    yield put({ type: actionTypes.fetchSuccess, payload: response.data });
    yield put(dialogActions.open('msa-review-esign'));
    if (response?.data?.kyc_document?.uuid) {
      yield put(actions.sendOtp(response?.data?.kyc_document?.uuid));
      yield put(merchantActions.fetch());
    }
  } catch (err) {
    const errResponse = yield err.response;
    yield put({ type: actionTypes.fetchFailed, payload: null });
    yield put(toastActions.show({
      variant: 'error',
      title: 'Error',
      content: errResponse?.data?.kyc_document || errResponse?.data?.error || 'Something went wrong',
    }))
  }
}

function* sendOtpWorker(action) {
  const { kycDocumentUuid } = action.payload;
  try {
    yield call(
      [axios, 'post'],
      API_ENDPOINTS.SERVICE_AGREEMENT.SEND_SINGNATORY_OTP({
        kycDocumentUuid,
        merchantUuid: AuthManager.getMerchantUUID(),
      }),
    ) || {};
  } catch{}
}

function* verifyOtpWorker(action) {
  const { otp, kycDocumentUuid } = action.payload;
  const { pancard_name } = yield select(merchantSelectors.getMerchantDetails) || {};
  try {
    const response = yield call(
      [axios, 'post'],
      API_ENDPOINTS.SERVICE_AGREEMENT.ESIGN_MERGED_DOCUMENT({
        kycDocumentUuid,
        merchantUuid: AuthManager.getMerchantUUID(),
      }),
      { otp },
    ) || {};
    yield put({ type: actionTypes.verifyOtpSuccess });
    yield put(dialogActions.close());
    yield delay(200);
    yield put(dialogActions.open(
      'msa-success',
      {
        downloadUrl: response?.data?.kyc_document?.processed_document?.path,
        name: pancard_name,
      }
    ));
    // yield put(merchantActions.updateMerchantDetails({ is_service_agreement_esigned: true }))
    yield put(uiActions.loadUI({ is_service_agreement_esigned: true }))
  } catch (err) {
    const errResponse = yield err.response;
    yield put({
      type: actionTypes.verifyOtpFailed,
      payload: (
        errResponse && errResponse.data && errResponse.data.error
      ) ? errResponse.data.error : 'Something went wrong.'
    });
  }
}

function* getEsignedAgreementWorker() {
  try {
    const params = { 'kyc_document_type[]': 'Agreement', 'status[]': 'Approved' };
    const response = yield call(
      [axios, 'get'],
      API_ENDPOINTS.SERVICE_AGREEMENT.ESIGNED_DOCUMENT(AuthManager.getMerchantUUID(), params),
    ) || {};
    yield put({ type: actionTypes.getEsignedAgreementSuccess, payload: response.data });
    if (response?.data?.kyc_documents?.length) {
      const documentPath = response.data.kyc_documents[0]?.processed_document?.path;
      if (documentPath) {
        window.open(documentPath, '_blank')
      }
    } else {
      yield put(toastActions.show({
        variant: 'error',
        title: 'Error',
        content: 'Esigned Agreement not found',
      }))
    }
  } catch (err) {
    const errResponse = yield err.response;
    yield put({ type: actionTypes.getEsignedAgreementFailed, payload: errResponse });
    yield put(toastActions.show({
      variant: 'error',
      title: 'Error',
      content: errResponse?.data?.kyc_document || 'Something went wrong',
    }))
  }
}

//  Sagas
export function* saga() {
  yield takeLatest(actionTypes.fetch, fetchWorker);
  yield takeLatest(actionTypes.sendOtp, sendOtpWorker);
  yield takeLatest(actionTypes.verifyOtp, verifyOtpWorker);
  yield takeLatest(actionTypes.getEsignedAgreement, getEsignedAgreementWorker);
};
